/* global HTMLDivElement */
/**
 * tasks-filter-bar.tsx
 * Filter chip bar for TasksView: priority, tag, project, and defer-reveal toggle.
 * Matches the Notes view's Calm Bento chip interaction pattern.
 */

import type { Priority } from "../types";
import { PRIO, PRIO_LABEL } from "./bento-ui";
import { useDragScroll } from "../lib/use-drag-scroll";

// ── Types ──────────────────────────────────────────────────────────────────────

export type TaskFilters = {
  priority: Priority | "all";
  tag: string;       // "Tất cả" = no filter
  project: string;   // "" = no filter (project id, or "" for all)
  showDeferred: boolean;
};

export const TASK_FILTERS_DEFAULT: TaskFilters = {
  priority: "all",
  tag: "Tất cả",
  project: "",
  showDeferred: false,
};

type Props = {
  filters: TaskFilters;
  onChange: (f: TaskFilters) => void;
  /** Unique tags derived from the current task list */
  availableTags: string[];
  /** { id, name } pairs for projects that have tasks */
  availableProjects: { id: string; name: string }[];
  /** How many deferred tasks exist (to label the toggle chip) */
  deferredCount: number;
};

// ── Priority chip definitions ──────────────────────────────────────────────────

const PRIO_CHIPS: { value: Priority | "all"; label: string }[] = [
  { value: "all",    label: "Tất cả" },
  { value: "high",   label: `● ${PRIO_LABEL["high"]}` },
  { value: "medium", label: `● ${PRIO_LABEL["medium"]}` },
  { value: "low",    label: `● ${PRIO_LABEL["low"]}` },
];

// ── Chip helper ────────────────────────────────────────────────────────────────

/** A single filter chip button styled per Calm Bento / Notes pattern. */
function Chip({
  active,
  onClick,
  children,
  dotColor,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  dotColor?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition ${
        active ? "gbrand text-white" : "bg-white text-ink-700"
      }`}
    >
      {dotColor && (
        <span
          className="inline-block h-2 w-2 shrink-0 rounded-full"
          style={{ background: active ? "#fff" : dotColor }}
        />
      )}
      {children}
    </button>
  );
}

// ── TasksFilterBar ─────────────────────────────────────────────────────────────

/**
 * Renders three rows of filter chips (priority / tag / project + defer toggle).
 * Filters combine AND-across-categories, OR-within-category.
 * All state lives in the parent (TasksView); this is a pure controlled component.
 */
export function TasksFilterBar({
  filters,
  onChange,
  availableTags,
  availableProjects,
  deferredCount,
}: Props) {
  const set = <K extends keyof TaskFilters>(key: K, value: TaskFilters[K]) =>
    onChange({ ...filters, [key]: value });

  // Desktop: grab-to-pan the hidden-scrollbar chip rows (touch scrolls natively).
  const tagScroll = useDragScroll<HTMLDivElement>();
  const projectScroll = useDragScroll<HTMLDivElement>();

  const isDefault =
    filters.priority === "all" &&
    filters.tag === "Tất cả" &&
    filters.project === "" &&
    !filters.showDeferred;

  return (
    <div className="mb-4 space-y-2.5">
      {/* Row 1: Priority chips */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-[11px] font-bold uppercase tracking-widest text-ink-500">
          Ưu tiên
        </span>
        {PRIO_CHIPS.map(({ value, label }) => (
          <Chip
            key={value}
            active={filters.priority === value}
            onClick={() => set("priority", value)}
            dotColor={value !== "all" ? PRIO[value] : undefined}
          >
            {/* Strip the "● " prefix when showing the dot separately */}
            {value !== "all" ? PRIO_LABEL[value] : label}
          </Chip>
        ))}
      </div>

      {/* Row 2: Tag chips — single horizontal-scroll line (no wrap) to stay compact */}
      {availableTags.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-[11px] font-bold uppercase tracking-widest text-ink-500">
            Thẻ
          </span>
          <div
            ref={tagScroll.ref}
            {...tagScroll.dragProps}
            className="no-scrollbar flex min-w-0 flex-1 cursor-grab items-center gap-2 overflow-x-auto pb-1 active:cursor-grabbing"
          >
            {["Tất cả", ...availableTags].map((t) => (
              <Chip
                key={t}
                active={filters.tag === t}
                onClick={() => set("tag", t)}
              >
                {t === "Tất cả" ? "Tất cả" : `#${t}`}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {/* Row 3: Project chips — single horizontal-scroll line; only when projects have tasks */}
      {availableProjects.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-[11px] font-bold uppercase tracking-widest text-ink-500">
            Dự án
          </span>
          <div
            ref={projectScroll.ref}
            {...projectScroll.dragProps}
            className="no-scrollbar flex min-w-0 flex-1 cursor-grab items-center gap-2 overflow-x-auto pb-1 active:cursor-grabbing"
          >
            <Chip active={filters.project === ""} onClick={() => set("project", "")}>
              Tất cả
            </Chip>
            {availableProjects.map(({ id, name }) => (
              <Chip
                key={id}
                active={filters.project === id}
                onClick={() => set("project", id)}
              >
                {name}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {/* Row 4: Defer toggle + reset — shown only when relevant */}
      <div className="flex flex-wrap items-center gap-2">
        {deferredCount > 0 && (
          <Chip
            active={filters.showDeferred}
            onClick={() => set("showDeferred", !filters.showDeferred)}
          >
            {filters.showDeferred
              ? `Ẩn việc hoãn`
              : `Hiện cả việc hoãn (${deferredCount})`}
          </Chip>
        )}
        {!isDefault && (
          <button
            onClick={() => onChange(TASK_FILTERS_DEFAULT)}
            className="rounded-full bg-ink-200/60 px-3.5 py-1.5 text-[12.5px] font-semibold text-ink-700 transition hover:bg-ink-200"
          >
            ✕ Xóa bộ lọc
          </button>
        )}
      </div>
    </div>
  );
}
